import { useCallback, type SelectHTMLAttributes } from "react";

import { Text } from "../Text";

interface FormSelectProps<T extends string> extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  "size"
> {
  label?: string;
  error?: string;
  options: { value: T; label: string }[];
  size?: "sm" | "md";
  onValue?: (value: T) => void;
}

const sizeClasses = {
  md: "px-3 py-2 text-xs",
  sm: "px-2.5 py-1.5 text-[10px]",
} as const;

/**
 * Shared form select component with consistent styling
 * Matches TaskActivityFilters reference tokens
 */
export const FormSelect = <T extends string>({
  label,
  error,
  options,
  size = "md",
  className = "",
  onValue,
  onChange,
  ...selectProps
}: FormSelectProps<T>) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e);
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      onValue?.(e.target.value as T);
    },
    [onChange, onValue],
  );

  return (
    <div>
      {label && (
        <Text
          variant="caption"
          as="label"
          className="mb-1 block font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400"
        >
          {label}
        </Text>
      )}
      <select
        {...selectProps}
        className={`w-full rounded-none border border-slate-300 bg-white font-mono text-slate-900 transition-colors focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-slate-700 dark:bg-arti-background dark:text-slate-100 ${sizeClasses[size]} ${className}`}
        onChange={handleChange}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1 font-mono text-[10px] font-bold text-rose-600 dark:text-rose-400">
          {error}
        </p>
      )}
    </div>
  );
};
